import { atom } from 'jotai';
import { atomWithRefresh } from 'jotai/utils';
import { fetchProjects } from '~/service/projectsService';
import { fetchExpressions } from '~/service/expressionsService';
import { fetchProductInstancesArray } from '~/service/productInstancesService';
import { sortExpressionsByBusinessRules } from '~/utils/expressionSortRules';
import log from '~/logger';
export const selectedGenderAtom = atom('mens');
function filterProjectsByGender(projects, gender) {
    return projects.filter((project) => {
        if (!project.genderVariations || project.genderVariations.length === 0) {
            return false;
        }
        return project.genderVariations.some((genderVar) => {
            const genderId = genderVar.id?.toLowerCase();
            if (gender === 'mens') {
                return genderId === 'mens' || genderId === 'unisex';
            }
            if (gender === 'womens') {
                return genderId === 'womens' || genderId === 'unisex';
            }
            return genderId === 'unisex';
        });
    });
}
function filterExpressionsByGender(expressions, gender) {
    return expressions.filter((expression) => {
        if (!expression.gender) {
            return false;
        }
        const genderId = expression.gender.id?.toLowerCase();
        if (gender === 'mens') {
            return genderId === 'mens' || genderId === 'unisex';
        }
        if (gender === 'womens') {
            return genderId === 'womens' || genderId === 'unisex';
        }
        return genderId === 'unisex';
    });
}
export const projectsDataAtom = atomWithRefresh(async () => {
    try {
        log.info('Fetching projects data for product matrix');
        const response = await fetchProjects({ count: 100 });
        if (!Array.isArray(response.objects)) {
            log.warn('fetchProjects returned non-array response:', typeof response.objects);
            return [];
        }
        log.info('Projects data loaded', { count: response.objects.length });
        return response.objects;
    }
    catch (error) {
        log.error('Failed to fetch projects', {
            error: error instanceof Error ? error.message : 'Unknown error'
        });
        return [];
    }
});
export const expressionsDataAtom = atomWithRefresh(async () => {
    try {
        log.info('Fetching expressions data for product matrix');
        const response = await fetchExpressions({ count: 100 });
        if (!Array.isArray(response.objects)) {
            log.warn('fetchExpressions returned non-array response:', typeof response.objects);
            return [];
        }
        log.info('Expressions data loaded', { count: response.objects.length });
        return response.objects;
    }
    catch (error) {
        log.error('Failed to fetch expressions', {
            error: error instanceof Error ? error.message : 'Unknown error'
        });
        return [];
    }
});
export const productInstancesDataAtom = atomWithRefresh(async () => {
    try {
        log.info('Fetching product instances data for product matrix');
        const response = await fetchProductInstancesArray({ count: 1000 });
        log.info('Product instances data loaded', { count: response.length });
        return response;
    }
    catch (error) {
        log.error('Failed to fetch product instances', {
            error: error instanceof Error ? error.message : 'Unknown error'
        });
        return [];
    }
});
export const filteredProductInstancesAtom = atom(async (get) => {
    const productInstances = await get(productInstancesDataAtom);
    const selectedGender = get(selectedGenderAtom);
    return productInstances.filter((instance) => {
        if (!instance.gender?.id) {
            return false;
        }
        const genderId = instance.gender.id.toLowerCase();
        if (selectedGender === 'mens') {
            return genderId === 'mens' || genderId === 'unisex';
        }
        if (selectedGender === 'womens') {
            return genderId === 'womens' || genderId === 'unisex';
        }
        return genderId === 'unisex';
    });
});
export const projectIdsWithInstancesAtom = atom(async (get) => {
    const filteredInstances = await get(filteredProductInstancesAtom);
    return new Set(filteredInstances.map((instance) => instance.projectId));
});
export const expressionIdsWithInstancesAtom = atom(async (get) => {
    const filteredInstances = await get(filteredProductInstancesAtom);
    return new Set(filteredInstances
        .map((instance) => instance.expressionId)
        .filter((id) => id !== null));
});
export const filteredProjectsAtom = atom(async (get) => {
    const projects = await get(projectsDataAtom);
    const selectedGender = get(selectedGenderAtom);
    const projectIdsWithInstances = await get(projectIdsWithInstancesAtom);
    return filterProjectsByGender(projects, selectedGender).filter((project) => projectIdsWithInstances.has(project.id));
});
export const filteredExpressionsAtom = atom(async (get) => {
    const expressions = await get(expressionsDataAtom);
    const selectedGender = get(selectedGenderAtom);
    const expressionIdsWithInstances = await get(expressionIdsWithInstancesAtom);
    const expressionsWithInstances = filterExpressionsByGender(expressions, selectedGender).filter((expression) => expressionIdsWithInstances.has(expression.id));
    return sortExpressionsByBusinessRules(expressionsWithInstances);
});
